import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { BarcodeWedgeService } from '../../../../services/barcode-wedge.service';

/**
 * "Using the shop's device" — waits for a scan from a USB / Bluetooth barcode
 * scanner attached to this machine (HID keyboard-wedge capture), then hands the
 * raw payload to the same lookup the camera scanner uses.
 *
 * Rendered as an overlay above the order-request modal, which stays open
 * underneath. After MANUAL_ENTRY_DELAY_MS with no scan, a manual-entry field
 * appears as a fallback; wedge capture keeps running alongside it.
 */

/** How long to wait for a scan before offering manual entry. */
const MANUAL_ENTRY_DELAY_MS = 30_000;

@Component({
  selector: 'app-vendor-membership-device-scan-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './vendor-membership-device-scan-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vendor-membership-device-scan-modal.component.scss'],
})
export class VendorMembershipDeviceScanModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible = false;
  @Input() isProcessing = false;

  @Output() close = new EventEmitter<void>();
  @Output() scanned = new EventEmitter<string>();

  @ViewChild('manualInput', { static: false }) manualInputRef?: ElementRef<HTMLInputElement>;

  showManualEntry = false;
  manualValue = '';

  private wedgeSubscription: Subscription | null = null;
  private manualEntryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private barcodeWedge: BarcodeWedgeService) {}

  get message(): string {
    if (this.isProcessing) {
      return 'Looking up membership…';
    }
    return "Complete the scan using your shop's device to retrieve your membership details";
  }

  get canSubmitManualValue(): boolean {
    return !this.isProcessing && this.manualValue.trim().length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isVisible']) {
      return;
    }

    if (this.isVisible) {
      this.startListening();
    } else {
      this.stopListening();
    }
  }

  ngOnDestroy(): void {
    this.stopListening();
  }

  onClose(): void {
    if (this.isProcessing) {
      return;
    }
    this.close.emit();
  }

  /**
   * Document-level so Escape works before the manual-entry field exists — until
   * then nothing inside the overlay is focusable, so a host `keydown` never fires.
   */
  @HostListener('document:keydown.escape')
  onEscapeKeydown(): void {
    if (!this.isVisible) {
      return;
    }
    this.onClose();
  }

  /** Reveal manual entry early rather than making staff wait out the timer. */
  onEnterManuallyClick(): void {
    this.revealManualEntry();
  }

  onManualSubmit(): void {
    if (!this.canSubmitManualValue) {
      return;
    }
    this.scanned.emit(this.manualValue.trim());
  }

  private startListening(): void {
    this.showManualEntry = false;
    this.manualValue = '';

    this.wedgeSubscription?.unsubscribe();
    this.wedgeSubscription = this.barcodeWedge.scans$.subscribe(value => {
      if (!this.isVisible || this.isProcessing) {
        return;
      }
      this.scanned.emit(value);
    });
    this.barcodeWedge.start();

    this.clearManualEntryTimer();
    this.manualEntryTimer = setTimeout(() => {
      this.manualEntryTimer = null;
      this.revealManualEntry();
    }, MANUAL_ENTRY_DELAY_MS);
  }

  private revealManualEntry(): void {
    if (this.showManualEntry) {
      return;
    }
    this.showManualEntry = true;
    this.clearManualEntryTimer();

    // Focus the field so a scan lands in it directly; wedge capture ignores
    // keystrokes from focused inputs, so only one path can submit.
    setTimeout(() => {
      this.manualInputRef?.nativeElement?.focus();
    }, 0);
  }

  private stopListening(): void {
    this.wedgeSubscription?.unsubscribe();
    this.wedgeSubscription = null;
    this.barcodeWedge.stop();
    this.clearManualEntryTimer();
    this.showManualEntry = false;
    this.manualValue = '';
  }

  private clearManualEntryTimer(): void {
    if (this.manualEntryTimer !== null) {
      clearTimeout(this.manualEntryTimer);
      this.manualEntryTimer = null;
    }
  }
}
